import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
    ResponsiveContainer, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';

const ChartTooltip = ({ active, payload, label, valueFormatter }) => {
    if (!active || !payload?.length) return null;
    // an entry can carry its own, longer tooltip title ("31 oct. · Aujourd'hui")
    label = payload[0].payload?.tooltipLabel ?? label;
    return (
        <div style={{
            background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
            boxShadow: '0 4px 14px rgba(16, 24, 40, 0.08)', padding: '8px 12px', fontSize: 12,
        }}>
            {label && <div style={{ fontWeight: 600, color: '#111827', marginBottom: 4 }}>{label}</div>}
            {payload.map((p, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#374151' }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: p.color || p.payload?.fill, flexShrink: 0 }} />
                    {valueFormatter ? valueFormatter(p.value) : `${p.name}: ${p.value}`}
                </div>
            ))}
        </div>
    );
};

const AXIS_TITLE_STYLE = { fontSize: 11, fontWeight: 600, fill: '#6b7280', textAnchor: 'middle' };
// slate rather than light gray: the axis values have to be readable at a glance
const TICK_STYLE = { fontSize: 11.5, fill: '#64748b' };
const GRID_STROKE = '#e5e7eb';

// Below this width per day the chart scrolls horizontally instead of squeezing every day in.
const MIN_POINT_WIDTH = 28;
// Extra room needed before a scrolling chart goes back to the single layout (see hysteresis below).
const SWITCH_BACK_MARGIN = 48;
// Roughly one x tick label per this many pixels.
const TICK_SPACING = 90;

// Compact tick labels ("40 k", "2,5 k", "1,3 M"): the full "40.000,00" made the axis heavy and
// hard to scan — exact amounts are in the tooltip.
const formatAxisNumber = (v) => new Intl.NumberFormat('fr-MA', { notation: 'compact', maximumFractionDigits: 1 }).format(v);

// 5 round y ticks (0 … max) — computed here rather than by Recharts so the fixed y-axis chart and
// the scrolling plot share exactly the same scale. Fine-grained steps so the top tick stays close
// to the peak (an 11 k peak gets a 12 k axis, not 20 k with half the plot empty).
const niceTicks = (data) => {
    const max = Math.max(0, ...data.map(d => Number(d.total) || 0));
    if (max === 0) return [0, 1, 2, 3, 4];
    // counts (e.g. number of sales): whole-number ticks only, never "0,5 vente"
    if (max < 4 && data.every(d => Number.isInteger(Number(d.total) || 0))) {
        return Array.from({ length: Math.ceil(max) + 1 }, (_, i) => i);
    }
    const raw = max / 4;
    const mag = 10 ** Math.floor(Math.log10(raw));
    const step = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].map(m => m * mag).find(s => s >= raw);
    return [0, 1, 2, 3, 4].map(i => +(i * step).toFixed(6));
};

// xAxisLabel / yAxisLabel: optional axis titles, so a reader knows what the two scales measure.
// yUnit: appended to every y tick ("14 000 dhs") — an alternative to a y-axis title.
//
// highlightColor: fill of the bars whose entry has `highlight: true` (e.g. today, still in progress).
//
// Long periods (more days than fit at MIN_POINT_WIDTH each) scroll horizontally: the y-axis stays
// pinned on the left while the days scroll, starting on the most recent ones. Mouse wheel over the
// plot scrolls it too (and hands back to page scrolling once an end is reached).
export default function SalesTrendChart({ data = [], valueFormatter, color = '#2563eb', highlightColor = '#93c5fd', xAxisLabel, yAxisLabel, yUnit }) {
    const formatAxisAmount = (v) => (yUnit ? `${formatAxisNumber(v)} ${yUnit}` : formatAxisNumber(v));
    const wrapRef = useRef(null);
    const scrollRef = useRef(null);
    const [width, setWidth] = useState(0);
    const [scrollbarHeight, setScrollbarHeight] = useState(0);

    // width fits "2,5 k dhs" on one line — Recharts wraps a tick that doesn't fit, which
    // pushed the top one above the plot and clipped it
    const yAxisWidth = (yAxisLabel ? 16 : 0) + (yUnit ? 68 : 44);
    // right: room for the last x label ("28 oct.") so it isn't cut at the edge
    const margin = { top: 14, right: 24, left: yAxisLabel ? 8 : 0, bottom: xAxisLabel ? 16 : 0 };
    const ticks = niceTicks(data);
    const domain = [0, ticks[ticks.length - 1]];

    const plotWidth = Math.max(0, width - yAxisWidth - margin.left);
    const contentWidth = data.length * MIN_POINT_WIDTH;
    // Hysteresis: switch to scrolling as soon as the days don't fit, but only back to the single
    // chart once there's clear room — a period sitting right at the threshold (e.g. 28 points)
    // can't then toggle between the two layouts.
    const scrollableRef = useRef(false);
    let scrollable = scrollableRef.current;
    if (width > 0) {
        if (!scrollable && contentWidth > plotWidth) scrollable = true;
        else if (scrollable && contentWidth <= plotWidth - SWITCH_BACK_MARGIN) scrollable = false;
    }
    scrollableRef.current = scrollable;

    useLayoutEffect(() => {
        const el = wrapRef.current;
        if (!el) return;
        // rounded: sub-pixel width changes must not flip the layout back and forth
        const ro = new ResizeObserver(([entry]) => setWidth(Math.round(entry.contentRect.width)));
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // Start on the most recent days — only when the period itself changes (the dashboard re-renders
    // for unrelated reasons and `data` is a fresh array each time, which used to yank the user back
    // to the end mid-browse). Also keeps the pinned axis aligned with the plot.
    const periodKey = `${data.length}|${data[0]?.day}|${data[data.length - 1]?.day}`;
    useLayoutEffect(() => {
        const el = scrollRef.current;
        if (!scrollable || !el) return;
        el.scrollLeft = el.scrollWidth;
        setScrollbarHeight(el.offsetHeight - el.clientHeight);
    }, [scrollable, periodKey]);

    // Drag to scroll (mouse): the scrollbar is hidden, so grabbing the plot and dragging sideways
    // moves through the days. Touch keeps native scrolling. A drag doesn't fire a click afterwards.
    const [dragging, setDragging] = useState(false);
    useEffect(() => {
        const el = scrollRef.current;
        if (!scrollable || !el) return;
        let startX = 0;
        let startLeft = 0;
        let active = false;
        const onDown = (e) => {
            if (e.pointerType !== 'mouse' || e.button !== 0) return;
            active = true;
            startX = e.clientX;
            startLeft = el.scrollLeft;
        };
        const onMove = (e) => {
            if (!active) return;
            const dx = e.clientX - startX;
            if (Math.abs(dx) > 3) setDragging(true);
            el.scrollLeft = startLeft - dx;
        };
        const onUp = () => {
            if (!active) return;
            active = false;
            setDragging(false);
        };
        el.addEventListener('pointerdown', onDown);
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        return () => {
            el.removeEventListener('pointerdown', onDown);
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };
    }, [scrollable]);

    // Vertical wheel scrolls the days while there's room in that direction (non-passive so it can
    // stop the page from scrolling at the same time).
    useEffect(() => {
        const el = scrollRef.current;
        if (!scrollable || !el) return;
        const onWheel = (e) => {
            if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return; // trackpad horizontal: native
            const atStart = el.scrollLeft <= 0;
            const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
            if ((e.deltaY < 0 && atStart) || (e.deltaY > 0 && atEnd)) return;
            el.scrollLeft += e.deltaY;
            e.preventDefault();
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [scrollable]);

    const yAxis = (hide) => (
        <YAxis
            hide={hide}
            domain={domain}
            ticks={ticks}
            interval={0}
            tick={TICK_STYLE}
            axisLine={false}
            tickLine={false}
            width={yAxisWidth}
            tickFormatter={formatAxisAmount}
            label={!hide && yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft', offset: 0, style: AXIS_TITLE_STYLE } : undefined}
        />
    );
    const xAxis = (interval) => (
        <XAxis
            dataKey="day"
            tick={TICK_STYLE}
            axisLine={false}
            tickLine={false}
            tickMargin={10}
            interval={interval}
            minTickGap={24}
            label={xAxisLabel ? { value: xAxisLabel, position: 'insideBottom', offset: -14, style: AXIS_TITLE_STYLE } : undefined}
        />
    );
    // One bar per day: daily totals are discrete, a smoothed curve invented dips and bumps
    // between days that never happened
    const bars = (
        <Bar
            dataKey="total"
            fill={color}
            name="Ventes"
            radius={[4, 4, 0, 0]}
            maxBarSize={28}
            animationDuration={500}
        >
            {data.map((d, i) => <Cell key={i} fill={d.highlight ? highlightColor : color} />)}
        </Bar>
    );
    const tooltip = (
        <Tooltip
            content={<ChartTooltip valueFormatter={valueFormatter} />}
            cursor={{ fill: 'rgba(148, 163, 184, 0.12)' }}
        />
    );

    // One stable, always-observed wrapper for both layouts — swapping the measured element itself
    // between modes made the observer report the detached one at 0px, flipping the layout back
    // and forth endlessly (the chart "vibrated" and its animation never finished).
    if (!scrollable) {
        // Everything fits: one chart, thinned to ~8 x labels so 90/365 days stay legible.
        const tickInterval = Math.max(0, Math.ceil(data.length / 8) - 1);
        return (
            <div ref={wrapRef} style={{ width: '100%', height: '100%', display: 'flex' }}>
                <div style={{ flex: 1, minWidth: 0, height: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data} margin={margin}>
                        <CartesianGrid vertical={false} stroke={GRID_STROKE} strokeDasharray="3 4" />
                        {xAxis(tickInterval)}
                        {yAxis(false)}
                        {tooltip}
                        {bars}
                    </BarChart>
                </ResponsiveContainer>
                </div>
            </div>
        );
    }

    return (
        <div ref={wrapRef} style={{ width: '100%', height: '100%', display: 'flex' }}>
            {/* pinned y-axis: same data, domain and margins as the plot, only the axis drawn;
                bottom padding = the plot's scrollbar so both share the same drawing height */}
            <div style={{ width: yAxisWidth + margin.left, flexShrink: 0, height: '100%', paddingBottom: scrollbarHeight, boxSizing: 'border-box' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data} margin={{ ...margin, right: 0 }}>
                        <XAxis dataKey="day" tick={false} axisLine={false} tickLine={false} tickMargin={10} />
                        {yAxis(false)}
                        {/* invisible series: Recharts only draws an axis's ticks when the chart has one */}
                        <Bar dataKey="total" fill="none" isAnimationActive={false} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <div ref={scrollRef} className={`sales-trend-scroll${dragging ? ' is-dragging' : ''}`} style={{ flex: 1, minWidth: 0, overflowX: 'auto', overflowY: 'hidden', height: '100%' }}>
                {/* 100% here already excludes the scrollbar (it shrinks the scroller's content box) */}
                <div style={{ width: contentWidth, height: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data} margin={{ ...margin, left: 0 }}>
                            <CartesianGrid vertical={false} stroke={GRID_STROKE} strokeDasharray="3 4" />
                            {xAxis(Math.max(0, Math.round(TICK_SPACING / MIN_POINT_WIDTH) - 1))}
                            {yAxis(true)}
                            {tooltip}
                            {bars}
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}
